import { Dialog } from "@headlessui/react"
import React, { useEffect, useState } from "react"
import Takedown from "../../../catalog/asset-registry/new-release-action/takedown/Takedown"
import { SelectOption, User, UserTakedown } from "../../../../../types"
import { NewReleaseActions, TakedownActionTabs } from "../../../../../actions/types"
import { KButton, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { UserTakedownIcon } from "../../../../ui/icons"
import { users } from "../../../../../apis"
import { toast } from "react-toastify"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"

enum RUN_TYPES {
  FAIL_ONLY = "fail_only",
  START_OVER = "start_over",
  CONTINUE = "continue"
}

enum UPLOAD_METHODS {
  ACR_CLOUD = "acr_cloud",
  ACR_CLOUD_AND_TITLE_MATCH = "acr_cloud_and_title_match",
  TITLE_MATCH = "title_match"
}

const AdminTakedownAudioModal = ({
  setTakedownAudioAsset,
  selectedUser,
  takedownAudioAsset
}: {
  selectedUser?: User
  takedownAudioAsset?: UserTakedown
  setTakedownAudioAsset: (takedownAudioAsset?: UserTakedown) => void
}) => {
  const [selectedTab, setSelectedTab] = useState(TakedownActionTabs.SELECT_ALBUM)
  const [selectedTakedownArtist, setSelectedTakedownArtist] = useState<SelectOption>()
  const [selectedRunType, setSelectedRunType] = useState<RUN_TYPES>(RUN_TYPES.FAIL_ONLY)
  const [selectedUploadMethod, setSelectedUploadMethod] = useState<UPLOAD_METHODS>(UPLOAD_METHODS.ACR_CLOUD)
  const [audioDurationMatch, setAudioDurationMatch] = useState<string>("1")

  useEffect(() => {
    if (!selectedUser) setSelectedTakedownArtist(undefined)
    setSelectedTakedownArtist({ label: selectedUser?.firstName || "", value: selectedUser?.artistSpotifyId || "" })
  }, [selectedUser])

  const onSubmit = () => {
    if (!takedownAudioAsset || !selectedUser) return

    if (!window.confirm("Are you sure to upload audio assets from drive?")) return

    users(`/user_takedown_start_asset_upload/`, "POST", {
      user_id: takedownAudioAsset.userId,
      id: takedownAudioAsset.id,
      run_type: selectedRunType,
      upload_method: selectedUploadMethod,
      audio_duration_match: parseInt(audioDurationMatch) || 1
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error(`Error: ${resp.data.message}`, errorFilledToastOptions)
        return
      }
      toast.success(resp.data.message, successFilledToastOptions)
      onClose()
    })
  }

  const onClose = () => {
    setTakedownAudioAsset(undefined)
    setSelectedRunType(RUN_TYPES.FAIL_ONLY)
    setSelectedUploadMethod(UPLOAD_METHODS.ACR_CLOUD)
  }

  const renderRadioOption = <T extends UPLOAD_METHODS | RUN_TYPES>(
    setSelected: React.Dispatch<React.SetStateAction<T>>,
    selected: T,
    value: T,
    label: string
  ) => {
    return (
      <div className="flex items-center">
        <input
          style={{ accentColor: "black", marginTop: 2 }}
          className={"cursor-pointer"}
          type="radio"
          checked={selected === value}
          onClick={() => setSelected(value)}
        />
        <div className={"ml-2"}>
          <KSpan color="black" text={label} />
        </div>
      </div>
    )
  }

  return (
    <Dialog open={takedownAudioAsset !== undefined && selectedUser !== undefined} onClose={() => onClose()}>
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d] ">
        <div className="p-6 shadow-md rounded-[10px] bg-[#fff]" style={{ maxHeight: "600px", overflowY: "scroll" }}>
          <div>
            <KTitleSpan fontSize={20} lineHeight="22px" text="Takedown Audio Assets" />
          </div>
          <div className="mb-4 mt-1">
            <KSpan text="Going to upload audio assets from drive." />
          </div>
          <div className="mb-1">
            <KSpan text="Included User(s):" />
          </div>
          <div>
            <KSpan
              color="#111"
              text={`- ${takedownAudioAsset?.user.firstName} ${takedownAudioAsset?.user.lastName} (${takedownAudioAsset?.user.email})`}
            />
          </div>
          {takedownAudioAsset?.userMembers?.map((userMember) => {
            return (
              <div key={`userMember_${userMember.id}`}>
                <KSpan color="#111" text={`- ${userMember.firstName} ${userMember.lastName} (${userMember.email})`} />
              </div>
            )
          }) || <div />}
          <div className="mt-6">
            <KSpan text="Select Upload Method:" />
          </div>
          <div className="mt-1 flex gap-8">
            {renderRadioOption(setSelectedUploadMethod, selectedUploadMethod, UPLOAD_METHODS.ACR_CLOUD, "ACR Cloud")}
            {/*renderRadioOption(
              setSelectedUploadMethod,
              selectedUploadMethod,
              UPLOAD_METHODS.ACR_CLOUD_AND_TITLE_MATCH,
              "ACR Cloud + Zip Album Title Match"
            )*/}
            {renderRadioOption(
              setSelectedUploadMethod,
              selectedUploadMethod,
              UPLOAD_METHODS.TITLE_MATCH,
              "Zip Album Title Match"
            )}
          </div>
          <div className="mt-6">
            <KSpan text="Select Run Type:" />
          </div>
          <div className="mt-1 flex gap-8">
            {renderRadioOption(setSelectedRunType, selectedRunType, RUN_TYPES.FAIL_ONLY, "Faily Only")}
            {renderRadioOption(setSelectedRunType, selectedRunType, RUN_TYPES.START_OVER, "Start Over")}
            <div className="flex gap-2 items-center mt-1">
              {renderRadioOption(setSelectedRunType, selectedRunType, RUN_TYPES.CONTINUE, `Continue with`)}
              <KSpan text={`(${takedownAudioAsset?.lastAssetUploadFilename || ""})`} />
            </div>
          </div>
          <div className="mt-6 flex gap-1 items-center">
            <KSpan text="Audio Duration Match (±X seconds):" />
            <KInput
              width={250}
              onChange={(text) => setAudioDurationMatch(text)}
              value={audioDurationMatch}
              placeholder="Enter a number. By default 1 second"
            />
          </div>

          <div className="flex gap-3 mt-8 justify-center items-center">
            <KButton text={"Cancel"} onClick={() => onClose()} background="#fff" shadowDisabled={true} />
            <KButton text={"Submit"} textColor="white" background="black" onClick={() => onSubmit()} />
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default AdminTakedownAudioModal
